import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import {
  ChevronDown,
  X,
  Check,
  Search,
  Users,
  AlertCircle,
  Calculator,
  Calendar,
} from 'lucide-react';
import { fadeInUp, staggerContainer } from '../utils/animations';

interface Patient {
  id: string;
  prenume: string;
  nume: string;
  consultation_status: string;
  serviciu: string;
}

interface Doctor {
  id: string;
  prenume: string;
  nume: string;
}

interface WorkLog {
  patientId: string;
  recommendationType:
    | 'medic'
    | 'navigator'
    | 'campanie'
    | 'medic30'
    | 'probono'
    | 'personalizare';
  selectedDoctor?: string;
  navigatorName?: string;
  medic30AllocationType?: 'campaign' | 'navigator';
  customValue?: number;
  participatingDoctors: string[];
}

interface FinancialSummary {
  doctorId: string;
  doctorName: string;
  amount: number;
}

interface NavigatorSummary {
  name: string;
  amount: number;
}

interface SaveResponse {
  success: boolean;
  error?: string;
}

const ALLOWED_EMAILS = [
  'ovidiu.mudura@yahoo.com',
  'tudorbolos2001@yahoo.com',
  'mihaicojocarulaboncz@yahoo.com',
  'sergiumurg@yahoo.com',
  'sergiu.murg@decizieoncologica.ro',
  'viman.diana@yahoo.ro',
  'flaviuscoser@gmail.com',
];

const PATIENT_VALUE = 1000;
const MAINTENANCE_PERCENTAGE = 0.1;
const ADMIN_PERCENTAGE = 0.1;
const RECOMMENDER_PERCENTAGE = 0.4;
const NAVIGATOR_PERCENTAGE = 0.1;
const CAMPAIGN_PERCENTAGE = 0.1;

function Financiar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [workLogs, setWorkLogs] = useState<WorkLog[]>([]);
  const [showNavigatorInput, setShowNavigatorInput] = useState(false);
  const [showDoctorsModal, setShowDoctorsModal] = useState(false);
  const [navigatorName, setNavigatorName] = useState('');
  const [currentLogIndex, setCurrentLogIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedDoctors, setTempSelectedDoctors] = useState<string[]>([]);
  const [sharedParticipatingDoctors, setSharedParticipatingDoctors] = useState<
    string[]
  >([]);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary[]>(
    []
  );
  const [navigatorSummary, setNavigatorSummary] = useState<NavigatorSummary[]>(
    []
  );
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    checkAccess();
  }, [navigate]);

  useEffect(() => {
    calculateFinancialSummary();
  }, [workLogs, doctors]);

  useEffect(() => {
    if (selectedDate) {
      loadExistingRecords(selectedDate);
    }
  }, [selectedDate]);

  const loadExistingRecords = async (date: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('financiar')
        .select(
          `
          *,
          patient:profiles!financiar_patient_id_fkey (
            id,
            prenume,
            nume
          ),
          recommender:profiles!financiar_recommender_id_fkey (
            id,
            prenume,
            nume
          )
        `
        )
        .eq('date', date);

      if (error) throw error;

      if (data && data.length > 0) {
        const logs: WorkLog[] = data.map((record) => ({
          patientId: record.patient_id,
          recommendationType: record.recommendation_type as
            | 'medic'
            | 'navigator'
            | 'campanie'
            | 'medic30'
            | 'personalizare',
          selectedDoctor: record.recommender_id || undefined,
          navigatorName: record.navigator_name || undefined,
          customValue: record.custom_value || undefined,
          participatingDoctors: record.participating_doctors,
        }));

        if (data[0].participating_doctors.length > 0) {
          setSharedParticipatingDoctors(data[0].participating_doctors);
        }

        setWorkLogs(logs);
      } else {
        setWorkLogs([]);
        setSharedParticipatingDoctors([]);
      }
    } catch (err) {
      console.error('Error loading records:', err);
      setError('Eroare la încărcarea înregistrărilor');
    } finally {
      setLoading(false);
    }
  };

  const checkAccess = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !ALLOWED_EMAILS.includes(user.email || '')) {
        navigate('/');
        return;
      }

      await Promise.all([fetchPatients(), fetchDoctors()]);
    } catch (error) {
      console.error('Error checking access:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      // Calculate date 6 days ago from today
      const sixDaysAgo = new Date();
      sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
      const sixDaysAgoISO = sixDaysAgo.toISOString();

      const { data, error } = await supabase
        .from('profiles')
        .select('id, prenume, nume, consultation_status, serviciu')
        .eq('role', 'pacient')
        .in('consultation_status', ['planned', 'closed'])
        .gte('planned_date', sixDaysAgoISO)
        .order('nume')
        .order('prenume');

      if (error) throw error;
      console.log('Fetched patients:', data); // Debug log
      setPatients(data || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setError('Error fetching patients');
    }
  };

  const fetchDoctors = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, prenume, nume')
        .eq('role', 'doctor')
        .order('nume')
        .order('prenume');

      if (error) throw error;
      setDoctors(data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setError('Error fetching doctors');
    }
  };

  const calculateFinancialSummary = () => {
    const summary = new Map<string, number>();
    doctors.forEach((doctor) => {
      summary.set(doctor.id, 0);
    });

    let adminAmount = 0;
    const navigatorAmounts = new Map<string, number>();

    workLogs.forEach((log) => {
      if (!log.patientId) return;

      // Skip financial calculations for pro-bono
      if (log.recommendationType === 'probono') {
        return;
      }

      // Handle personalizare separately (no admin fee)
      if (log.recommendationType === 'personalizare') {
        if (log.customValue && log.customValue > 0) {
          // Distribute entire custom value equally among participating doctors
          const sharePerDoctor = log.customValue / log.participatingDoctors.length;
          log.participatingDoctors.forEach((doctorId) => {
            summary.set(
              doctorId,
              (summary.get(doctorId) || 0) + sharePerDoctor
            );
          });
        }
        return; // Skip the rest of the calculation for personalizare
      }

      // For all other recommendation types, calculate with admin fee
      const baseAmount = PATIENT_VALUE;
      adminAmount += baseAmount * ADMIN_PERCENTAGE;
      const availableAmount =
        baseAmount * (1 - MAINTENANCE_PERCENTAGE - ADMIN_PERCENTAGE);

      switch (log.recommendationType) {
        case 'medic': {
          if (log.selectedDoctor) {
            summary.set(
              log.selectedDoctor,
              (summary.get(log.selectedDoctor) || 0) +
                baseAmount * RECOMMENDER_PERCENTAGE
            );

            const remainingAmount =
              availableAmount - baseAmount * RECOMMENDER_PERCENTAGE;
            const sharePerDoctor =
              remainingAmount / log.participatingDoctors.length;
            log.participatingDoctors.forEach((doctorId) => {
              summary.set(
                doctorId,
                (summary.get(doctorId) || 0) + sharePerDoctor
              );
            });
          }
          break;
        }

        case 'navigator': {
          if (log.navigatorName) {
            const navigatorAmount = baseAmount * NAVIGATOR_PERCENTAGE;
            navigatorAmounts.set(
              log.navigatorName,
              (navigatorAmounts.get(log.navigatorName) || 0) + navigatorAmount
            );

            const sharePerDoctor =
              (availableAmount - navigatorAmount) /
              log.participatingDoctors.length;
            log.participatingDoctors.forEach((doctorId) => {
              summary.set(
                doctorId,
                (summary.get(doctorId) || 0) + sharePerDoctor
              );
            });
          }
          break;
        }

        case 'campanie': {
          const campaignAmount = baseAmount * CAMPAIGN_PERCENTAGE;
          navigatorAmounts.set(
            'Campanie publicitară',
            (navigatorAmounts.get('Campanie publicitară') || 0) + campaignAmount
          );

          const sharePerDoctor =
            (availableAmount - campaignAmount) /
            log.participatingDoctors.length;
          log.participatingDoctors.forEach((doctorId) => {
            summary.set(
              doctorId,
              (summary.get(doctorId) || 0) + sharePerDoctor
            );
          });
          break;
        }

        case 'medic30': {
          if (log.selectedDoctor) {
            // Allocate 30% to selected doctor
            const medic30Amount = baseAmount * 0.3;
            summary.set(
              log.selectedDoctor,
              (summary.get(log.selectedDoctor) || 0) + medic30Amount
            );

            // Allocate 10% based on allocation type
            const allocationAmount = baseAmount * 0.1;
            if (
              log.medic30AllocationType === 'navigator' &&
              log.navigatorName
            ) {
              navigatorAmounts.set(
                log.navigatorName,
                (navigatorAmounts.get(log.navigatorName) || 0) +
                  allocationAmount
              );
            } else {
              // Default to campaign
              navigatorAmounts.set(
                'Campanie publicitară',
                (navigatorAmounts.get('Campanie publicitară') || 0) +
                  allocationAmount
              );
            }

            // Distribute remaining amount to participating doctors
            const remainingAmount =
              availableAmount - medic30Amount - allocationAmount;
            const sharePerDoctor =
              remainingAmount / log.participatingDoctors.length;
            log.participatingDoctors.forEach((doctorId) => {
              summary.set(
                doctorId,
                (summary.get(doctorId) || 0) + sharePerDoctor
              );
            });
          }
          break;
        }

      }
    });

    const summaryArray: FinancialSummary[] = Array.from(summary.entries()).map(
      ([doctorId, amount]) => {
        const doctor = doctors.find((d) => d.id === doctorId);
        return {
          doctorId,
          doctorName: doctor ? `${doctor.nume} ${doctor.prenume}` : 'Unknown',
          amount: Math.round(amount),
        };
      }
    );

    if (adminAmount > 0) {
      summaryArray.push({
        doctorId: 'admin',
        doctorName: 'Administrare platformă - 10%',
        amount: Math.round(adminAmount),
      });
    }

    const navigatorArray: NavigatorSummary[] = Array.from(
      navigatorAmounts.entries()
    ).map(([name, amount]) => ({
      name,
      amount: Math.round(amount),
    }));

    summaryArray.sort((a, b) => b.amount - a.amount);
    navigatorArray.sort((a, b) => b.amount - a.amount);

    setFinancialSummary(summaryArray);
    setNavigatorSummary(navigatorArray);
  };

  const handleSave = async () => {
    if (workLogs.length === 0) {
      setError('Nu există înregistrări de salvat');
      return;
    }

    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const invalidLogs = workLogs.filter((log) => {
        if (!log.patientId) return true;
        if (log.recommendationType === 'probono') {
          // For probono, only patient and participating doctors are required
          if (log.participatingDoctors.length === 0) return true;
          return false;
        }
        if (
          (log.recommendationType === 'medic' ||
            log.recommendationType === 'medic30') &&
          !log.selectedDoctor
        )
          return true;
        if (log.recommendationType === 'navigator' && !log.navigatorName)
          return true;
        if (log.recommendationType === 'personalizare') {
          // For personalizare, custom value and participating doctors are required
          if (!log.customValue || log.customValue <= 0) return true;
        }
        if (log.participatingDoctors.length === 0) return true;
        return false;
      });

      if (invalidLogs.length > 0) {
        setError(
          'Toate câmpurile sunt obligatorii pentru fiecare înregistrare'
        );
        return;
      }

      // First delete existing records for this date
      const { error: deleteFinanciarError } = await supabase
        .from('financiar')
        .delete()
        .eq('date', selectedDate);

      if (deleteFinanciarError) throw deleteFinanciarError;

      const { error: deleteCalcError } = await supabase
        .from('financial_calculations')
        .delete()
        .eq('date', selectedDate);

      if (deleteCalcError) throw deleteCalcError;

      // Then save the work logs
      const records = workLogs.map((log) => ({
        date: selectedDate,
        patient_id: log.patientId,
        recommendation_type: log.recommendationType,
        recommender_id:
          log.recommendationType === 'medic' ||
          log.recommendationType === 'medic30'
            ? log.selectedDoctor
            : null,
        navigator_name:
          log.recommendationType === 'navigator'
            ? log.navigatorName
            : log.recommendationType === 'campanie'
            ? 'Campanie publicitară'
            : log.recommendationType === 'medic30' &&
              log.medic30AllocationType === 'navigator'
            ? log.navigatorName
            : log.recommendationType === 'medic30'
            ? 'Campanie publicitară'
            : null,
        custom_value: log.recommendationType === 'personalizare' ? log.customValue : null,
        participating_doctors: log.participatingDoctors,
      }));

      const { error: insertError } = await supabase
        .from('financiar')
        .insert(records);

      if (insertError) throw insertError;

      // Then save the financial calculations
      const calculations = [];

      // Count non-pro-bono and non-personalizare work logs for admin amount
      const nonProBonoLogs = workLogs.filter(
        (log) => log.recommendationType !== 'probono' && log.recommendationType !== 'personalizare'
      );
      const adminAmount =
        nonProBonoLogs.length * PATIENT_VALUE * ADMIN_PERCENTAGE;
      calculations.push({
        date: selectedDate,
        amount: adminAmount,
        type: 'admin',
      });

      // Process each work log (skip pro-bono entirely)
      workLogs.forEach((log) => {
        if (log.recommendationType === 'probono') {
          return; // Skip all financial calculations for pro-bono
        }

        const baseAmount = PATIENT_VALUE;
        const availableAmount =
          baseAmount * (1 - MAINTENANCE_PERCENTAGE - ADMIN_PERCENTAGE);

        // Add recommender amount if applicable
        if (log.recommendationType === 'medic' && log.selectedDoctor) {
          calculations.push({
            date: selectedDate,
            doctor_id: log.selectedDoctor,
            amount: baseAmount * RECOMMENDER_PERCENTAGE,
            type: 'recommender',
          });
        }

        // Add navigator amount if applicable
        if (log.recommendationType === 'navigator' && log.navigatorName) {
          calculations.push({
            date: selectedDate,
            amount: baseAmount * NAVIGATOR_PERCENTAGE,
            type: 'navigator',
            navigator_name: log.navigatorName,
          });
        }

        // Add campaign amount if applicable
        if (log.recommendationType === 'campanie') {
          calculations.push({
            date: selectedDate,
            amount: baseAmount * CAMPAIGN_PERCENTAGE,
            type: 'campaign',
          });
        }

        // Add medic30 amounts if applicable
        if (log.recommendationType === 'medic30' && log.selectedDoctor) {
          // Add 30% to selected doctor
          calculations.push({
            date: selectedDate,
            doctor_id: log.selectedDoctor,
            amount: baseAmount * 0.3,
            type: 'recommender',
          });

          // Add 10% based on allocation type
          const allocationAmount = baseAmount * 0.1;
          if (log.medic30AllocationType === 'navigator' && log.navigatorName) {
            calculations.push({
              date: selectedDate,
              amount: allocationAmount,
              type: 'navigator',
              navigator_name: log.navigatorName,
            });
          } else {
            // Default to campaign
            calculations.push({
              date: selectedDate,
              amount: allocationAmount,
              type: 'campaign',
            });
          }
        }

        // Handle personalizare type separately
        if (log.recommendationType === 'personalizare' && log.customValue && log.customValue > 0) {
          // Distribute entire custom value equally among participating doctors
          const sharePerDoctor = log.customValue / log.participatingDoctors.length;
          log.participatingDoctors.forEach((doctorId) => {
            calculations.push({
              date: selectedDate,
              doctor_id: doctorId,
              amount: sharePerDoctor,
              type: 'doctor',
            });
          });
        } else {
          // Calculate remaining amount for participating doctors for other types
          const remainingAmount =
            availableAmount -
            (log.recommendationType === 'medic'
              ? baseAmount * RECOMMENDER_PERCENTAGE
              : log.recommendationType === 'navigator'
              ? baseAmount * NAVIGATOR_PERCENTAGE
              : log.recommendationType === 'campanie'
              ? baseAmount * CAMPAIGN_PERCENTAGE
              : log.recommendationType === 'medic30'
              ? baseAmount * 0.3 + baseAmount * 0.1
              : 0);

          // Distribute remaining amount among participating doctors
          const sharePerDoctor =
            remainingAmount / log.participatingDoctors.length;
          log.participatingDoctors.forEach((doctorId) => {
            calculations.push({
              date: selectedDate,
              doctor_id: doctorId,
              amount: sharePerDoctor,
              type: 'doctor',
            });
          });
        }
      });

      // Save financial calculations
      const { error: calcError } = await supabase
        .from('financial_calculations')
        .insert(calculations);

      if (calcError) throw calcError;

      // Clear form and show success message
      setWorkLogs([]);
      setSharedParticipatingDoctors([]);
      setFinancialSummary([]);
      setNavigatorSummary([]);
      setSaveSuccess(true);

      // Reload the records to show the latest data
      await loadExistingRecords(selectedDate);

      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving records:', err);
      setError('Eroare la salvarea înregistrărilor');
    } finally {
      setSaving(false);
    }
  };

  const addNewLog = () => {
    setWorkLogs((prev) => [
      ...prev,
      {
        patientId: '',
        recommendationType: 'medic',
        participatingDoctors: sharedParticipatingDoctors,
      },
    ]);
  };

  const updateLog = (index: number, updates: Partial<WorkLog>) => {
    setWorkLogs((prev) =>
      prev.map((log, i) => (i === index ? { ...log, ...updates } : log))
    );
  };

  const handleNavigatorInput = (index: number) => {
    setCurrentLogIndex(index);
    setNavigatorName('');
    setShowNavigatorInput(true);
  };

  const saveNavigatorName = () => {
    if (currentLogIndex >= 0) {
      updateLog(currentLogIndex, { navigatorName });
    }
    setShowNavigatorInput(false);
    setNavigatorName('');
    setCurrentLogIndex(-1);
  };

  const handleOpenDoctorsModal = () => {
    setTempSelectedDoctors(sharedParticipatingDoctors);
    setShowDoctorsModal(true);
  };

  const handleSaveSelectedDoctors = () => {
    setSharedParticipatingDoctors(tempSelectedDoctors);
    setWorkLogs((prev) =>
      prev.map((log) => ({
        ...log,
        participatingDoctors: tempSelectedDoctors,
      }))
    );
    setShowDoctorsModal(false);
    setSearchTerm('');
  };

  const toggleDoctorSelection = (doctorId: string) => {
    setTempSelectedDoctors((prev) =>
      prev.includes(doctorId)
        ? prev.filter((id) => id !== doctorId)
        : [...prev, doctorId]
    );
  };

  const filteredDoctors = doctors.filter((doctor) =>
    `${doctor.nume} ${doctor.prenume}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Se încarcă...</div>
      </div>
    );
  }

  const nonZeroFinancialSummary = financialSummary.filter(
    (summary) => summary.amount > 0
  );
  const nonZeroNavigatorSummary = navigatorSummary.filter(
    (summary) => summary.amount > 0
  );

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-8 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div
            className="flex justify-between items-center mb-6"
            variants={fadeInUp}
          >
            <h1 className="text-2xl font-bold text-gray-900">
              Pontaj Comisie Medicală
            </h1>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#024994] focus:border-[#024994]"
              />
            </div>
          </motion.div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {saveSuccess && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <p className="text-green-700">
                Înregistrările au fost salvate cu succes
              </p>
            </div>
          )}

          <motion.div
            className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
            variants={fadeInUp}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-medium text-gray-900">
                  Medici participanți
                </h2>
              </div>
              <button
                onClick={handleOpenDoctorsModal}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#024994]"
              >
                {sharedParticipatingDoctors.length > 0
                  ? `${sharedParticipatingDoctors.length} medici selectați`
                  : 'Selectează medici'}
              </button>
            </div>
            {sharedParticipatingDoctors.length === 0 && (
              <div className="mt-2 flex items-center gap-2 text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>
                  Selectați medicii participanți înainte de a adăuga
                  înregistrări
                </span>
              </div>
            )}
          </motion.div>

          <motion.div className="overflow-x-auto" variants={fadeInUp}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nume pacient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recomandare
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workLogs.map((log, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={log.patientId}
                        onChange={(e) =>
                          updateLog(index, { patientId: e.target.value })
                        }
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#024994] focus:border-[#024994] sm:text-sm rounded-md"
                      >
                        <option value="">Selectează pacient</option>
                        {patients.map((patient) => (
                          <option key={patient.id} value={patient.id}>
                            {patient.nume} {patient.prenume}
                            {patient.serviciu?.toLowerCase() === 'probono'
                              ? ' (ProBono)'
                              : patient.serviciu?.toLowerCase() === 'asociatie'
                              ? ' (NaviCare)'
                              : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <select
                          value={log.recommendationType}
                          onChange={(e) =>
                            updateLog(index, {
                              recommendationType: e.target
                                .value as WorkLog['recommendationType'],
                              selectedDoctor: undefined,
                              navigatorName: undefined,
                              customValue: undefined,
                            })
                          }
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#024994] focus:border-[#024994] sm:text-sm rounded-md"
                        >
                          <option value="medic">Medic 40%</option>
                          <option value="medic30">Medic 30%</option>
                          <option value="navigator">Navigator 10%</option>
                          <option value="campanie">
                            Campanie publicitară 10%
                          </option>
                          <option value="probono">Pro-Bono (0%)</option>
                          <option value="personalizare">Personalizare</option>
                        </select>

                        {(log.recommendationType === 'medic' ||
                          log.recommendationType === 'medic30') && (
                          <select
                            value={log.selectedDoctor}
                            onChange={(e) =>
                              updateLog(index, {
                                selectedDoctor: e.target.value,
                              })
                            }
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#024994] focus:border-[#024994] sm:text-sm rounded-md"
                          >
                            <option value="">Selectează medic</option>
                            {doctors.map((doctor) => (
                              <option key={doctor.id} value={doctor.id}>
                                {doctor.nume} {doctor.prenume}
                              </option>
                            ))}
                          </select>
                        )}

                        {log.recommendationType === 'medic30' && (
                          <select
                            value={log.medic30AllocationType || 'campaign'}
                            onChange={(e) =>
                              updateLog(index, {
                                medic30AllocationType: e.target.value as
                                  | 'campaign'
                                  | 'navigator',
                              })
                            }
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#024994] focus:border-[#024994] sm:text-sm rounded-md"
                          >
                            <option value="campaign">Campanie 10%</option>
                            <option value="navigator">Navigator 10%</option>
                          </select>
                        )}

                        {log.recommendationType === 'navigator' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleNavigatorInput(index)}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#024994]"
                            >
                              {log.navigatorName || 'Adaugă navigator'}
                            </button>
                          </div>
                        )}

                        {log.recommendationType === 'medic30' &&
                          log.medic30AllocationType === 'navigator' && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleNavigatorInput(index)}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#024994]"
                              >
                                {log.navigatorName || 'Adaugă navigator'}
                              </button>
                            </div>
                          )}

                        {log.recommendationType === 'personalizare' && (
                          <input
                            type="number"
                            min="0"
                            max="1000000"
                            step="1"
                            value={log.customValue || ''}
                            onChange={(e) =>
                              updateLog(index, {
                                customValue: e.target.value ? Number(e.target.value) : undefined,
                              })
                            }
                            placeholder="Valoare personalizată"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#024994] focus:border-[#024994] sm:text-sm rounded-md"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          <motion.div
            className="mt-4 flex justify-end gap-2"
            variants={fadeInUp}
          >
            <button
              onClick={addNewLog}
              disabled={sharedParticipatingDoctors.length === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#024994] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#024994] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Adaugă înregistrare
            </button>
            <button
              onClick={handleSave}
              disabled={saving || workLogs.length === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Se salvează...' : 'Salvează'}
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          className="bg-white rounded-lg shadow-lg p-6"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-50px' }}
          variants={staggerContainer}
        >
          <motion.div
            className="flex items-center gap-2 mb-6"
            variants={fadeInUp}
          >
            <Calculator className="w-6 h-6 text-[#024994]" />
            <h2 className="text-2xl font-bold text-gray-900">
              Situație financiară
            </h2>
          </motion.div>

          <motion.div className="overflow-x-auto" variants={fadeInUp}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nume medic
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sumă de plată
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recomandare 10%
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sumă de plată
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {nonZeroFinancialSummary
                  .filter(
                    (summary) =>
                      summary.doctorName !== 'Administrare platformă - 10%'
                  )
                  .map((summary, index) => (
                    <tr key={summary.doctorId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {summary.doctorName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {summary.amount.toLocaleString('ro-RO')} RON
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {nonZeroNavigatorSummary[index]?.name || ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {nonZeroNavigatorSummary[index]?.amount
                          ? `${nonZeroNavigatorSummary[
                              index
                            ].amount.toLocaleString('ro-RO')} RON`
                          : ''}
                      </td>
                    </tr>
                  ))}
                {nonZeroFinancialSummary
                  .filter(
                    (summary) =>
                      summary.doctorName === 'Administrare platformă - 10%'
                  )
                  .map((adminSummary) => (
                    <tr key="admin">
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {adminSummary.doctorName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {adminSummary.amount.toLocaleString('ro-RO')} RON
                      </td>
                    </tr>
                  ))}
                {nonZeroNavigatorSummary
                  .slice(nonZeroFinancialSummary.length)
                  .map((nav, index) => (
                    <tr key={`nav-${index}`}>
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {nav.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {nav.amount.toLocaleString('ro-RO')} RON
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </motion.div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showNavigatorInput && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Introduceți numele navigatorului
                </h3>
                <button
                  onClick={() => setShowNavigatorInput(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <input
                type="text"
                value={navigatorName}
                onChange={(e) => setNavigatorName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#024994] focus:border-[#024994]"
                placeholder="Nume navigator"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowNavigatorInput(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Anulează
                </button>
                <button
                  onClick={saveNavigatorName}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#024994] rounded-md hover:opacity-90"
                >
                  Salvează
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showDoctorsModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Selectează medici participanți
                </h3>
                <button
                  onClick={() => setShowDoctorsModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="relative mb-4">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Caută medici..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#024994] focus:border-[#024994]"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>

              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {filteredDoctors.map((doctor) => (
                    <label
                      key={doctor.id}
                      className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={tempSelectedDoctors.includes(doctor.id)}
                        onChange={() => toggleDoctorSelection(doctor.id)}
                        className="h-5 w-5 text-[#024994] border-gray-300 rounded focus:ring-[#024994]"
                      />
                      <span className="ml-3 text-gray-900">
                        {doctor.nume} {doctor.prenume}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setShowDoctorsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Anulează
                </button>
                <button
                  onClick={handleSaveSelectedDoctors}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#024994] rounded-md hover:opacity-90"
                >
                  Salvează
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Financiar;
